import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, cn, Flex, Icon, Text } from '@kognitos/lattice';

import type { IAssetView } from '@/modules/library/types';
import { SelectOrUploadFileDialog } from '@/modules/library/components/SelectOrUploadFileDialog';

export interface IRainAttachFileCardProps {
  /**
   * - `running`  — awaiting the user: a trigger opening the file picker.
   * - `success`  — files were attached: a read-only marker (with names).
   * - `error`    — the attach was cancelled / failed.
   */
  status: 'running' | 'success' | 'error';
  /** Optional agent-supplied reason shown alongside the trigger. */
  reason?: string;
  /** Names of the files that ended up attached (success marker). */
  attachedNames?: string[];
  /** True while registration is writing (picker confirm in flight). */
  isSubmitting?: boolean;
  /** Register the chosen existing assets as Knowledge Files, then resolve. */
  onConfirm: (assets: IAssetView[]) => void;
  /** Upload picked local files, register them, then resolve. */
  onUploadFiles: (files: File[]) => void;
  /** Dismiss the picker without attaching — fails the tool call gracefully. */
  onCancel: () => void;
}

/**
 * The Agent Builder's `attach_file` card: a picker-backed card (mirrors the
 * connection-picker `add_integration` card) that lets the user choose existing
 * assets or upload new ones, which are then registered as agent Knowledge
 * Files. Self-contained dialog open state so the message builder can render it
 * as pure `customContent`.
 */
export function RainAttachFileCard({
  status,
  reason,
  attachedNames,
  isSubmitting,
  onConfirm,
  onUploadFiles,
  onCancel,
}: IRainAttachFileCardProps) {
  const { t } = useTranslation('agent-builder');
  const [pickerOpen, setPickerOpen] = useState(false);

  if (status === 'success') {
    const names = attachedNames ?? [];
    return (
      <div
        className="rounded-lg border border-border bg-card p-4"
        data-testid="rain-attach-file-card"
        data-variant="resolved"
      >
        <Flex direction="row" align="center" gap={2}>
          <div className="rounded-md bg-success/10 p-2">
            <Icon type="CircleCheck" size="sm" className="text-success" />
          </div>
          <Text weight="medium" level="small">
            {names.length > 0
              ? t('agentBuilder.config.files.attachedNamed', {
                  names: names.join(', '),
                })
              : t('agentBuilder.config.files.attached')}
          </Text>
        </Flex>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className="rounded-lg border border-border bg-card p-4"
        data-testid="rain-attach-file-card"
        data-variant="cancelled"
      >
        <Flex direction="row" align="center" gap={2}>
          <Icon type="X" size="sm" className="text-muted-foreground" />
          <Text color="muted" level="small">
            {t('agentBuilder.config.files.attachCancelled')}
          </Text>
        </Flex>
      </div>
    );
  }

  // running — a trigger into the picker.
  return (
    <Flex direction="column" gap={2}>
      {reason ? <Text level="base">{reason}</Text> : null}
      <Button
        variant="ghost"
        onClick={() => setPickerOpen(true)}
        disabled={isSubmitting}
        aria-label={t('agentBuilder.config.files.addFiles')}
        data-testid="rain-attach-file-card"
        data-variant="trigger"
        className={cn(
          'h-auto w-full justify-between gap-3 rounded-lg border border-border bg-card p-4 text-left',
          'hover:bg-accent/50',
        )}
      >
        <Flex direction="row" align="center" gap={2}>
          <div className="rounded-md bg-primary/10 p-2">
            <Icon type="FileText" size="sm" className="text-primary" />
          </div>
          <Text weight="medium" level="small">
            {t('agentBuilder.config.files.addFiles')}
          </Text>
        </Flex>
        <Icon type="ChevronRight" size="sm" className="text-muted-foreground" />
      </Button>

      <SelectOrUploadFileDialog
        isOpen={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          onCancel();
        }}
        onConfirm={assets => {
          setPickerOpen(false);
          onConfirm(assets);
        }}
        onUploadFiles={files => {
          setPickerOpen(false);
          onUploadFiles(files);
        }}
      />
    </Flex>
  );
}
